import { endTracking, getPreset, isInlinePreset } from '@account/account-portal/config'
import { getRuntimeInfo, updateRuntimeInfo, useRuntimeInfo } from '@account/account-portal/lib'
import { AccountPortalProps } from '@account/account-portal/types'
import icons from '@adl/asset-icons/dist/sprite.svg?raw'
import { GlApp } from '@adl/component-app'
import { ABTestingProvider } from '@crystal/ab-testing'
import { ConfigProvider, Context } from '@crystal/config'
import { SvgSprite, Microfrontend, MicrofrontendProps, getQueryParams, getCookies, isServer } from '@crystal/core'
import { FeatureFlagsProvider } from '@crystal/feature-flags'
import { TranslationsProvider } from '@crystal/translator'
import { FC, useEffect, useMemo } from 'react'

import styles from './adl-utils.module.scss'
import kakaoIcon from './assets/icons/kakao-icon.svg?raw'
import naverIcon from './assets/icons/naver-icon.svg?raw'
import { AccountPortal as AccountPortalComponent } from './components/AccountPortal'
import { accountPortalConfig } from './config'
import { ViewDataProvider } from './lib'

const FEATURE_FLAGS_PROJECT_NAME = 'account-acquisition'
export const TRANSLATIONS_PROJECT_NAME = 'account-portal'
export const TRANSLATIONS_NAMESPACE_NAME = 'default'

export const AccountPortal: FC<MicrofrontendProps<AccountPortalProps>> = props => {
  useMemo(() => {
    const runtime = getRuntimeInfo()
    updateRuntimeInfo({
      isAuthProvider: getPreset(props.preset).isAuthProvider,
      preset: props.preset,
      touchpoint: runtime.touchpoint ?? props.touchpoint,
      propSourceId: props.sourceId ?? runtime.propSourceId,
    })
  }, [])

  useEffect(() => {
    endTracking('overlay_loaded')
  }, [])

  const { isModalVisible } = useRuntimeInfo()
  const isInlineView = isInlinePreset(props.preset)
  const { translation } = getPreset(props.preset)

  if (!isInlineView && !isModalVisible && !isServer()) {
    return null
  }

  return (
    <GlApp className={styles.adlUtils}>
      <Microfrontend {...props}>
        <SvgSprite id="gl-theme-icons" source={icons} />
        <SvgSprite id="kakao-icon" source={kakaoIcon} />
        <SvgSprite id="naver-icon" source={naverIcon} />
        <FeatureFlagsProvider context={getFlagContext(props.context)} project={FEATURE_FLAGS_PROJECT_NAME}>
          <ConfigProvider context={props.context} config={accountPortalConfig}>
            <ABTestingProvider>
              <TranslationsProvider
                context={props.context}
                project={TRANSLATIONS_PROJECT_NAME}
                namespace={translation || TRANSLATIONS_NAMESPACE_NAME}>
                <ViewDataProvider preset={props.preset}>
                  <AccountPortalComponent {...props} />
                </ViewDataProvider>
              </TranslationsProvider>
            </ABTestingProvider>
          </ConfigProvider>
        </FeatureFlagsProvider>
      </Microfrontend>
    </GlApp>
  )
}

function getFlagContext(context: Context) {
  // allow to override feature flags from query params and cookies
  // this is useful for testing purposes
  const flagContext = {
    ...getCookies(),
    ...getQueryParams(),
    ...context,
  }
  return flagContext
}
